import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdPersonSearch } from 'react-icons/md';
import {
  AppInput,
  AppSelect,
  OutlineButton,
  PrimaryButton,
  StatusAlertCard,
  StatusBadge,
} from '../../../../components/ui';
import { listAppUsers } from '../../../../repositories/appUser.repository';
import { recommendIndentLine } from '../../../../repositories/indent.repository';
import {
  labelsMatch,
  lineNeedLabel,
  matchBadgeProps,
  personCapabilityLabels,
  personIsMatch,
  personMatchKind,
} from './recommendMatch';
import { ReferredByPicker } from './ReferredByPicker';

/**
 * Recommend people (app users) for material / special_service lines.
 * Table UI + Matches only / All filter + services visibility.
 */
export function RecommendPeopleModal({
  isOpen,
  onClose,
  request,
  line,
  onSaved,
  maxRecommendations = 3,
}) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [matchFilter, setMatchFilter] = useState('match');
  const [selected, setSelected] = useState(() => new Set());
  const [remarks, setRemarks] = useState({});
  const [referrals, setReferrals] = useState({});
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const needLabel = lineNeedLabel(line);
  const lineType = line?.lineType || '';
  const capabilityTitle =
    lineType === 'material' ? 'Materials' : 'Services';

  const existingIds = useMemo(
    () =>
      new Set(
        (line?.recommendations || [])
          .map((r) => r.appUserId || r.personId)
          .filter((id) => id != null)
          .map(Number),
      ),
    [line],
  );

  const slots = Math.max(
    0,
    maxRecommendations - (line?.recommendations?.length || 0),
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set());
    setRemarks({});
    setReferrals({});
    setSearch('');
    setMatchFilter(needLabel ? 'match' : 'all');
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await listAppUsers({ pageSize: 200 });
        if (cancelled) return;
        setPeople(res.items || []);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load users',
            status: 'error',
            position: 'top-right',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, toast, needLabel]);

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = (people || [])
      .filter((p) => !existingIds.has(Number(p.id)))
      .map((p) => ({ person: p, kind: personMatchKind(p, line) }))
      .filter(({ person: p, kind }) => {
        if (matchFilter === 'match' && kind !== 'exact') return false;
        if (!q) return true;
        const caps = personCapabilityLabels(p, line).join(' ');
        const hay = [
          p.fullName,
          p.ownerName,
          p.tradeName,
          p.phone,
          p.village,
          p.userTypeLabel,
          caps,
          ...(p.specialServices || []),
          ...(p.offerings || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });

    rows.sort((a, b) => {
      const rank = (k) => (k === 'exact' ? 0 : k === 'partial' ? 1 : 2);
      const byMatch = rank(a.kind) - rank(b.kind);
      if (byMatch !== 0) return byMatch;
      const an =
        a.person.tradeName || a.person.fullName || a.person.ownerName || '';
      const bn =
        b.person.tradeName || b.person.fullName || b.person.ownerName || '';
      return an.localeCompare(bn);
    });
    return rows;
  }, [people, search, existingIds, matchFilter, line]);

  const matchCount = useMemo(
    () =>
      (people || []).filter(
        (p) => !existingIds.has(Number(p.id)) && personIsMatch(p, line),
      ).length,
    [people, existingIds, line],
  );

  if (!request || !line) return null;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      if (next.size >= slots) {
        toast({
          title: `Maximum ${maxRecommendations} recommendations per line`,
          status: 'warning',
          position: 'top-right',
        });
        return prev;
      }
      next.add(id);
      return next;
    });
  };

  const handleRecommend = async () => {
    const ids = [...selected];
    if (!ids.length) {
      toast({ title: 'Select at least one person', status: 'warning', position: 'top-right' });
      return;
    }
    setSaving(true);
    try {
      let latest = null;
      for (const appUserId of ids) {
        const referral = referrals[appUserId] || {};
        latest = await recommendIndentLine(request.id, line.id, {
          appUserId,
          remark: remarks[appUserId] || '',
          referredByAppUserId: referral.appUserId || null,
          referredByName: referral.name || '',
        });
      }
      toast({
        title: `${ids.length} recommendation(s) added`,
        status: 'success',
        position: 'top-right',
      });
      onSaved?.(latest);
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to recommend',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const lineLabel = needLabel || 'line';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      isCentered
      scrollBehavior="inside"
      trapFocus={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader pb={2}>
          Recommend people / suppliers
          <Text fontSize="14px" fontWeight="500" color="text.secondary" mt={1}>
            Line: {lineLabel} · {request.requestNo || request.indentId}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
          <VStack align="stretch" spacing={4}>
            <Flex gap={3} wrap="wrap" align="end">
              <Box minW={{ base: '100%', sm: '260px' }} flex="1">
                <AppInput
                  label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name, services, phone, village…"
                />
              </Box>
              <Box minW={{ base: '100%', sm: '240px' }}>
                <AppSelect
                  label="Show"
                  value={matchFilter}
                  onChange={(v) => setMatchFilter(v || 'match')}
                  options={[
                    {
                      value: 'match',
                      label: needLabel
                        ? `Matches only · ${needLabel}`
                        : 'Matches only',
                    },
                    { value: 'all', label: 'All people' },
                  ]}
                />
              </Box>
            </Flex>
            <Text fontSize="13px" color="text.secondary">
              {loading
                ? 'Loading…'
                : `${candidates.length} shown · ${matchCount} match${matchCount === 1 ? '' : 'es'}`}{' '}
              · {selected.size} of {slots} slots
            </Text>
            {slots === 0 ? (
              <StatusAlertCard
                title={`Recommendation limit reached (${maxRecommendations})`}
                body="Remove an existing recommendation on this line before adding another."
              />
            ) : null}
            <Box
              borderWidth="1px"
              borderColor="border.subtle"
              borderRadius="md"
              overflow="hidden"
            >
              <Box overflowX="auto">
                <Table variant="simple" size="md" sx={{ tableLayout: 'auto' }}>
                  <Thead bg="bg.input">
                    <Tr>
                      <Th w="52px" px={3} />
                      <Th whiteSpace="nowrap">Match</Th>
                      <Th minW="180px">Person</Th>
                      <Th minW="260px">{capabilityTitle}</Th>
                      <Th whiteSpace="nowrap">Phone</Th>
                      <Th whiteSpace="nowrap">Type</Th>
                      <Th minW="140px">Area</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {candidates.map(({ person: p, kind }) => {
                      const isOn = selected.has(p.id);
                      const isFull = !isOn && selected.size >= slots;
                      const name =
                        p.tradeName ||
                        p.fullName ||
                        p.ownerName ||
                        `User #${p.id}`;
                      const badge = matchBadgeProps(kind);
                      const caps = personCapabilityLabels(p, line);
                      const area = [p.village, p.taluka, p.district]
                        .filter(Boolean)
                        .filter((v, i, arr) => arr.indexOf(v) === i)
                        .join(', ');
                      return (
                        <React.Fragment key={p.id}>
                          <Tr
                            cursor={isFull ? 'not-allowed' : 'pointer'}
                            opacity={isFull ? 0.5 : 1}
                            bg={isOn ? 'bg.amberTint' : undefined}
                            onClick={() => toggle(p.id)}
                          >
                            <Td
                              w="52px"
                              px={3}
                              py={3}
                              verticalAlign="top"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                size="lg"
                                isChecked={isOn}
                                isDisabled={isFull}
                                onChange={() => toggle(p.id)}
                                colorScheme="yellow"
                              />
                            </Td>
                            <Td py={3} whiteSpace="nowrap" verticalAlign="top">
                              <StatusBadge status={badge.status}>
                                {badge.label}
                              </StatusBadge>
                            </Td>
                            <Td py={3} verticalAlign="top" minW="180px">
                              <Text fontWeight="700" fontSize="15px">
                                {name}
                              </Text>
                              {p.tradeName && p.fullName ? (
                                <Text fontSize="13px" color="text.secondary" mt={0.5}>
                                  {p.fullName}
                                </Text>
                              ) : null}
                            </Td>
                            <Td py={3} verticalAlign="top" minW="260px">
                              {caps.length ? (
                                <Text fontSize="14px" lineHeight="1.45">
                                  {caps.map((label, idx) => {
                                    const isMatch = labelsMatch(label, needLabel);
                                    return (
                                      <React.Fragment key={`${label}-${idx}`}>
                                        {idx > 0 ? (
                                          <Text as="span" color="text.secondary">
                                            ,{' '}
                                          </Text>
                                        ) : null}
                                        <Text
                                          as="span"
                                          fontWeight={isMatch ? '700' : '500'}
                                          color={
                                            isMatch
                                              ? 'status.success'
                                              : 'text.primary'
                                          }
                                        >
                                          {label}
                                        </Text>
                                      </React.Fragment>
                                    );
                                  })}
                                </Text>
                              ) : (
                                <Text fontSize="14px" color="text.secondary">
                                  —
                                </Text>
                              )}
                            </Td>
                            <Td
                              py={3}
                              fontSize="14px"
                              whiteSpace="nowrap"
                              verticalAlign="top"
                            >
                              {p.phone || '—'}
                            </Td>
                            <Td py={3} whiteSpace="nowrap" verticalAlign="top">
                              <StatusBadge status="info">
                                {p.userTypeLabel || 'User'}
                              </StatusBadge>
                            </Td>
                            <Td py={3} verticalAlign="top" minW="140px">
                              <Text fontSize="14px">{area || '—'}</Text>
                            </Td>
                          </Tr>
                          {isOn ? (
                            <Tr bg="bg.amberTint">
                              <Td />
                              <Td colSpan={6} pt={0} pb={3}>
                                <Flex
                                  gap={3}
                                  align="flex-start"
                                  wrap={{ base: 'wrap', md: 'nowrap' }}
                                >
                                  <Box flex={{ base: '1 1 100%', md: '0 0 35%' }} minW={0}>
                                    <ReferredByPicker
                                      value={
                                        referrals[p.id] || {
                                          appUserId: null,
                                          name: '',
                                        }
                                      }
                                      excludeUserId={p.id}
                                      onChange={(next) =>
                                        setReferrals((prev) => ({
                                          ...prev,
                                          [p.id]: next,
                                        }))
                                      }
                                    />
                                  </Box>
                                  <Box flex={{ base: '1 1 100%', md: '1 1 65%' }} minW={0}>
                                    <AppInput
                                      label="Recommendation remark"
                                      value={remarks[p.id] || ''}
                                      onChange={(e) =>
                                        setRemarks((prev) => ({
                                          ...prev,
                                          [p.id]: e.target.value,
                                        }))
                                      }
                                      maxLength={250}
                                      placeholder="Why this person / supplier"
                                      h="44px"
                                      size="md"
                                    />
                                  </Box>
                                </Flex>
                              </Td>
                            </Tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                    {!loading && candidates.length === 0 ? (
                      <Tr>
                        <Td colSpan={7}>
                          <Text color="text.secondary" py={8} textAlign="center">
                            {matchFilter === 'match'
                              ? `No users list “${lineLabel}” yet. Switch to All people to browse everyone.`
                              : 'No people found. Try clearing search.'}
                          </Text>
                        </Td>
                      </Tr>
                    ) : null}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton
            leftIcon={<MdPersonSearch />}
            onClick={handleRecommend}
            isDisabled={selected.size < 1}
            isLoading={saving}
          >
            Recommend ({selected.size})
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
